import React, { useCallback, useMemo, useState } from 'react';
import { Formik } from 'formik';
import * as Yup from 'yup';
import {
  FiltersContainer,
  FilterSection,
  FilterSectionTitle,
  FilterGroup,
  FilterLabel,
  ActiveFiltersIndicator,
  FilterCount,
  FilterGroupTitle,
  Filters,
  MembersListFiltersGlobalStyle,
} from './styles';
import Input from '../FormikForm/Input';
import ReactSelect from '../FormikForm/ReactSelect';
import Submit from '../FormikForm/Submit';
import Button from '../Button';
import Collapse from '../Collpase';
import Icon from '../Icon';
import Loader from '../Loader';
import { useWindowSize } from '../../hooks/useWindowSize';
import filterIcon from '../../assets/icons/filter.svg';

// Tipos para los filtros
export interface FilterOption {
  value: string | number;
  label: string;
  isDisabled?: boolean;
}

export interface FilterGroup {
  title: string;
  filters: FilterConfig[];
}

export interface FilterConfig {
  name: string;
  label: string;
  type: 'select' | 'input' | 'multi';
  options?: FilterOption[];
  placeholder?: string;
  required?: boolean;
  dependsOn?: string; // Campo del que depende
  disabled?: boolean; // Si el campo debe estar deshabilitado
}

export interface MembersListFiltersProps {
  filtersGroup: FilterGroup[];
  loading?: boolean;
  initialValues?: Record<string, any>;
  title?: string;
  showClearButton?: boolean;
  appliedFilters?: any; // Para trackear filtros activos
  onSubmit: (values: any) => void;
  onClear?: () => void;
  onChange?: (values: any, setFieldValue: any) => void;
  getOptionsForField?: (fieldName: string, currentValues: any) => any[];
}

const Component: React.FC<MembersListFiltersProps> = ({
  filtersGroup,
  loading = false,
  initialValues = {},
  title = 'Filtros',
  showClearButton = true,
  appliedFilters = {},
  onSubmit,
  onClear,
  onChange,
  getOptionsForField,
}) => {
  const { isMobile } = useWindowSize();
  const [isExpanded, setIsExpanded] = useState(!isMobile);
  const [previousValues, setPreviousValues] = useState<Record<string, any>>({});

  // Contar filtros activos
  const getActiveFiltersCount = () => {
    return Object.keys(appliedFilters).filter(key => {
      const value = appliedFilters[key];
      if (Array.isArray(value)) {
        return value.length > 0;
      }
      return value && value !== '';
    }).length;
  };

  // Generar schema de validación dinámicamente
  const generateSchema = () => {
    const schemaFields: Record<string, any> = {};

    filtersGroup.forEach(filterGroup => {
      filterGroup.filters.forEach(filter => {
        if (filter.required) {
          schemaFields[filter.name] = Yup.string().required(`${filter.label} es requerido`);
        }
      })
    });

    return Yup.object().shape(schemaFields);
  };

  // Generar valores iniciales
  const getInitialValues = () => {
    const values: Record<string, any> = { ...initialValues };

    filtersGroup.forEach(filterGroup => {
      filterGroup.filters.forEach(filter => {
        if (!values[filter.name]) {
          values[filter.name] = '';
        }
      })
    });

    return values;
  };

  const handleSubmitFn = (values: any, actions: any) => {
    // Filtrar valores vacíos pero mantener arrays vacíos para campos multi
    const filteredValues = Object.keys(values).reduce((acc, key) => {
      const value = values[key];
      // Mantener el valor si no está vacío, o si es un array (incluso vacío) para campos multi
      if (value && value !== '' || Array.isArray(value)) {
        acc[key] = value;
      }
      return acc;
    }, {} as Record<string, any>);

    onSubmit(filteredValues);
    // Removemos actions.resetForm() para mantener los valores seleccionados
  };

  const handleClearFn = () => {
    if (onClear) {
      onClear();
    }
  };

  const handleChangeFn = (values: any, setFieldValue) => {
    // Verificar si hay campos dependientes que necesitan resetearse
    const updatedValues = { ...values };
    let hasChanges = false;

    filtersGroup.forEach(filterGroup => {
      filterGroup.filters.forEach(filter => {
        if (filter.dependsOn) {
          // Si el campo del que depende cambió, resetear este campo
          const dependentFieldValue = values[filter.dependsOn];
          const previousDependentValue = previousValues[filter.dependsOn];
          const currentFieldValue = values[filter.name];

          // Si el campo del que depende cambió de valor, resetear el campo dependiente
          if (dependentFieldValue !== previousDependentValue && currentFieldValue) {
            updatedValues[filter.name] = filter.type === 'multi' ? [] : '';
            hasChanges = true;
          }
        }
      });
    });

    // Actualizar los valores anteriores para la próxima comparación
    setPreviousValues(values);

    if (onChange) {
      onChange(hasChanges ? updatedValues : values, setFieldValue);
    }
  }

  const renderFilter = useCallback(({ filter, values, errors, touched, handleChange, handleBlur, setFieldValue }: { filter: FilterConfig, values: any, errors: any, touched: any, handleChange: any, handleBlur: any, setFieldValue: any }) => {
    // Determinar si el campo debe estar deshabilitado
    const isDisabled = Boolean(filter.disabled || loading || (filter.dependsOn && !values[filter.dependsOn]));

    // Obtener opciones dinámicamente si hay una función disponible
    const dynamicOptions = getOptionsForField ? getOptionsForField(filter.name, values) : null;
    const finalOptions = dynamicOptions || filter.options || [];

    switch (filter.type) {
      case 'select':
        return (
          <ReactSelect
            name={filter.name}
            placeholder={filter.placeholder || `Seleccionar ${filter.label.toLowerCase()}`}
            items={finalOptions}
            error={errors[filter.name]}
            touched={touched[filter.name]}
            onChange={(event) => {
              handleChange(event)
              // Usar los valores actualizados después del cambio
              const updatedValues = { ...values, [event.target.name]: event.target.value };
              handleChangeFn(updatedValues, setFieldValue)
            }}
            onBlur={handleBlur}
            options={{
              label: filter.label,
              marginBottom: 0,
              loading: loading,
            }}
            isOptionDisabled={(option) => {
              console.log("isOptionDisabled", option)
              return option.isDisabled
            }}
            isClearable
            isDisabled={isDisabled}
          />
        );

      case 'multi':

        return (
          <ReactSelect
            name={filter.name}
            placeholder={filter.placeholder || `Seleccionar ${filter.label.toLowerCase()}`}
            items={finalOptions}
            error={errors[filter.name]}
            touched={touched[filter.name]}
            onChange={(event) => {
              handleChange(event)
              // Usar los valores actualizados después del cambio
              console.log(event.target)
              const updatedValues = { ...values, [event.target.name]: [...event.target.value] };
              handleChangeFn(updatedValues, setFieldValue)
            }}
            onBlur={handleBlur}
            options={{
              label: filter.label,
              marginBottom: 0,
              loading: loading,
            }}
            isOptionDisabled={(option) => option.isDisabled}
            isMulti
            isClearable
            isDisabled={isDisabled}
          />
        );

      case 'input':
        return (
          <Input
            name={filter.name}
            placeholder={filter.placeholder || `Ingresar ${filter.label.toLowerCase()}`}
            value={values[filter.name]}
            error={errors[filter.name]}
            touched={touched[filter.name]}
            onChange={(event) => {
              handleChange(event)
              // Usar los valores actualizados después del cambio
              const updatedValues = { ...values, [event.target.name]: event.target.value };
              handleChangeFn(updatedValues, setFieldValue)
            }}
            onBlur={handleBlur}
            options={{
              label: filter.label,
              marginBottom: 0
            }}
            disabled={isDisabled}
          />
        );

      default:
        return null;
    }
  }, [loading])

  // Inicializar valores anteriores cuando el componente se monta
  React.useEffect(() => {
    const initialValues = getInitialValues();
    setPreviousValues(initialValues);
  }, []);

  // Crear el toggle label con indicador de filtros activos
  const toggleLabel = (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', gap: '12px' }}>
      <Icon icon={filterIcon} color="#6C5CE7" size="20px" />

      <span>{title}</span>

      {getActiveFiltersCount() > 0 && (
        <ActiveFiltersIndicator>
          <FilterCount>{getActiveFiltersCount()}</FilterCount>
          {getActiveFiltersCount() === 1 ? 'filtro activo' : 'filtros activos'}
        </ActiveFiltersIndicator>
      )}
    </div>
  );

  return (
    <>
      <MembersListFiltersGlobalStyle />
      <FiltersContainer className="members-list-filters">
        <Collapse
          toggleLabel={toggleLabel}
          startCollapsed={true}
          style={{
            padding: 0,
            marginBottom: '20px',
            background: '#fff',
            borderRadius: '8px',
            boxShadow: "none"
          }}
        >
          <FilterSection>
            <Formik
              initialValues={getInitialValues()}
              validationSchema={generateSchema()}
              onSubmit={handleSubmitFn}
              enableReinitialize
            >
              {({ handleSubmit, resetForm, values, errors, touched, handleChange, handleBlur, setFieldValue }) => {
                return (
                  <form
                    id="filters-form"
                    onSubmit={handleSubmit}
                  >
                    {filtersGroup.map((filterGroup, index) => {
                      return (
                        <React.Fragment key={JSON.stringify(filterGroup)}>
                          <FilterGroup>
                            {filterGroup.title && <FilterGroupTitle>{filterGroup.title}</FilterGroupTitle>}

                            <div style={{ height: 8 }} />

                            <Filters>
                              {filterGroup.filters.map((filter) => {
                                return (
                                  <FilterLabel key={filter.name}>
                                    {renderFilter({ filter, values, errors, touched, handleChange, handleBlur, setFieldValue })}
                                  </FilterLabel>
                                )
                              })}
                            </Filters>
                          </FilterGroup>

                          {index !== filtersGroup.length - 1 && <div style={{ height: 32 }} />}
                        </React.Fragment>
                      )
                    })}

                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', marginTop: '20px' }}>
                      <Submit
                        isSubmmiting={loading}
                        form="filters-form"
                        options={{
                          type: "filled",
                          skin: "purple",
                          size: "md",
                        }}
                      >
                        Aplicar filtros
                      </Submit>

                      {showClearButton && (
                        <Button
                          type="button"
                          onClick={() => {
                            resetForm();
                            handleClearFn();
                          }}
                          disabled={loading}
                          options={{
                            type: "outline",
                            skin: "purple",
                            size: "md",
                          }}
                        >
                          Limpiar
                        </Button>
                      )}
                    </div>
                  </form>
                )
              }
              }
            </Formik>
          </FilterSection>
        </Collapse>

        {loading && (
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(255, 255, 255, 0.8)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: '8px',
            zIndex: 10
          }}>
            <Loader color="#6C5CE7" />
          </div>
        )}
      </FiltersContainer>
    </>
  );
};

export default Component; 